'use client'
import type { FC } from 'react'
import { useMemo, useState } from 'react'
import { Skeleton } from '@chakra-ui/react'
import { EmptyMessage, GrandSection } from 'ui'
import type { WidgetBlogCategory } from 'ecosystem'
import { useBlogPostsBySlugRequest } from '../hooks'
import type { IBlogPostsProps } from './BlogPosts'
import { BlogNavigation, BlogPosts } from './index'

export interface BlogStdPageConfig {
  widgetBlogCategories: WidgetBlogCategory[]
  overrides?: {
    title?: string
    noBlogsMsg?: string
  }
  paths: {
    blogPostsBySlug: string
    blogPage?: string
  }
}

interface BlogPageStdProps extends Pick<IBlogPostsProps, 'customComponents'> {
  config: BlogStdPageConfig
}

const BlogPageStd: FC<BlogPageStdProps> = ({
  config: { overrides, widgetBlogCategories, paths },
  customComponents
}) => {
  const [selectedCategory, setSelectedCategory] = useState<WidgetBlogCategory | null>(null)
  const relatedSlugs = useMemo(
    () => selectedCategory?.relatedBlogPosts.map((rbp) => rbp.slug || ''),
    [selectedCategory?.relatedBlogPosts]
  )
  const {
    data: blogPostsWidgets,
    error,
    isLoading
  } = useBlogPostsBySlugRequest(paths.blogPostsBySlug, relatedSlugs)

  if (error) {
    return <EmptyMessage text="Oops! Something happened" />
  }

  return (
    <GrandSection
      contentContainerProps={{
        paddingTop: {
          base: 6,
          md: 4
        },
        spacing: {
          base: 8,
          md: 10
        }
      }}
      headingTag="h1"
      minH="800px"
      position="relative"
      sectionId="blog navigation"
      title={overrides?.title || 'Blogg'}
      titleProps={{
        containerProps: {
          margin: 0
        }
      }}>
      <BlogNavigation items={widgetBlogCategories} onSelect={setSelectedCategory} />
      <Skeleton isLoaded={!isLoading} minH="800px">
        {blogPostsWidgets ? (
          <BlogPosts customComponents={customComponents} widgets={blogPostsWidgets} />
        ) : (
          <EmptyMessage text={overrides?.noBlogsMsg || 'Inga inlägg hittades'} />
        )}
      </Skeleton>
    </GrandSection>
  )
}

export default BlogPageStd
